import { useState, useEffect } from "react";
import "./QuestionWorkflow.css";
import { Question, QuestionType, UpdateQuestionPayload } from "effno-core";
import { 
    Condition,
    ConditionActionType,
    ConditionOptionType,
    CONDITIONS_INITIAL,
    ConditionType
} from "./questionWorkflowConditions";
import { useSelector } from "react-redux";
import { selectEditingForm } from "../../redux/features/appData";
import { apiUpdateQuestion } from "../../services/QuestionService";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { getQuestionOrder } from "../shared/uiHelpers";
import { isEmptyJSON } from "../../utils/utils";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FormQuestion } from "../form-question/FormQuestion";
import { FormQuestionTag } from "../form-question/FormQuestionTag";
import { GenericSelect, GenericSelectOption } from "../form-widgets/GenericSelect";
import { QuestionSelect } from "../form-widgets/QuestionSelect";
import { CirclePlus } from "lucide-react";
import { optionCSS } from "react-select/dist/declarations/src/components/Option";

const VALUE_LOW = 0;
const VALUE_HIGH = 1;

type QuestionWorkflowProps = {
    question: Question;
    onChange: (question: Question) => void;
}

export const QuestionWorkflow = ({question, onChange} : QuestionWorkflowProps) => {

    const editingForm = useSelector(selectEditingForm);

    const [conditions, setConditions] = useState<Condition[]>([]);
    const [conditionOptions, setConditionOptions] = useState<ConditionOptionType[] | undefined>(undefined);
    const [destinationQuestionId, setDestinationQuestionId] = useState<string | undefined>(undefined);
    const [choices, setChoices] = useState<string[] | undefined>(undefined);
    const [savable, setSavable] = useState<boolean>(false);

    // expects JSON for conditions:
    // {
    //     conditions: []
    // }

    useEffect(() => {
        const questionConditions = JSON.parse(question.conditions) as Condition[] | undefined;

        if (questionConditions && !isEmptyJSON(questionConditions)) {
            setConditions(questionConditions as Condition[]);
        } else {
            setConditions([] as Condition[]);
        }

        setConditionOptions([...CONDITIONS_INITIAL[question.type]]);   
        const questionChoices = JSON.parse(question.settings).choices;
        setChoices(questionChoices);
        setSavable(false);
    }, [question]);

    const handleChangeOption = (type: ConditionType, index: number) => {
        const newConditions = [...conditions];
        newConditions[index].type = type;
        setConditions(newConditions);
        setSavable(true);
    }

    const handleChangeConditionValue = (value: string, index: number) => { 
        const newConditions = [...conditions];
        newConditions[index].values = [value];
        setConditions(newConditions);
        setSavable(true);
    }

    const handleChangeConditionMultiValue = (value: string, index: number, valueIndex: number) => { 
        const newConditions = [...conditions];
        const values = [...newConditions[index].values]
        values[valueIndex] = value;
        newConditions[index].values = values;
        setConditions(newConditions);
        setSavable(true);
    }

    const handleAddCondition = () => {
        const newConditions = (conditions && conditions.length > 0) ? [...conditions] : [];

        // figure out the default jump to, first ordered question
        const { nonEndQuestions, endQuestions } = getQuestionOrder(editingForm?.questions, editingForm?.questionOrder);
        const orderedQuestions = [...nonEndQuestions, ...endQuestions];
        
        newConditions.push({
            type: CONDITIONS_INITIAL[question.type][0].value,
            values: [],
            actionType: ConditionActionType.JUMP,
            destinationQuestionId: orderedQuestions[0].id,
        } as Condition);

        setConditions(newConditions);
        setSavable(true);
    }

    const handleDeleteCondition = (index: number) => {
        const newConditions = [...conditions];
        newConditions.splice(index, 1);
        setConditions(newConditions);
        setSavable(true);
    }

    const handleChangeDestination = (id: string, index: number) => {
        setDestinationQuestionId(id);
        const newConditions = [...conditions];
        newConditions[index].destinationQuestionId = id;
        setConditions(newConditions);
        setSavable(true);
    }

    const handleSave = async () => {
        await saveConditions();
    }

    const handleSelectChoice = (choice: string, conditionIndex: number) => {
        const newConditions = [...conditions];
        const choiceIndex = newConditions[conditionIndex].values.indexOf(choice);
        if (choiceIndex === -1) {
            newConditions[conditionIndex].values.push(choice);
        } else {
            newConditions[conditionIndex].values.splice(choiceIndex, 1);
        }
        setConditions(newConditions);
        setSavable(true);
    }

    const isChoiceSelected = (choice: string, conditionIndex: number) => {
        return conditions[conditionIndex].values.includes(choice);
    }

    const saveConditions = async () => {
        const updatedQuestion = {...question};
        updatedQuestion.conditions = JSON.stringify(conditions);

        console.log("[QuestionWorkflow] updatedQuestion: ", updatedQuestion);

        onChange(updatedQuestion);

        // now save to DB
        const payload = {
            questionId: updatedQuestion.id,
            type: updatedQuestion.type,
            title: updatedQuestion.title,
            description: updatedQuestion.description,
            required: updatedQuestion.required,
            settings: updatedQuestion.settings,
            conditions: updatedQuestion.conditions
        } as UpdateQuestionPayload

        const responseQuestion = await apiUpdateQuestion(payload);

        if (!responseQuestion) {
            toast("Failed to save conditions", toastFail);
            return;
        }

        toast("Conditions saved", toastSuccess);
        setSavable(false)
        return;
    }

    // order the questions
    const { nonEndQuestions, endQuestions } = getQuestionOrder(editingForm?.questions, editingForm?.questionOrder);
    // const orderedQuestions = [...nonEndQuestions, ...endQuestions].filter((q: Question) => {
    //     return q.id !== question.id;
    // })
    const orderedQuestions = [...nonEndQuestions, ...endQuestions];
    
    const isProhibited = question.type === QuestionType.SECTION || 
        question.type === QuestionType.CONTACT || 
        question.type === QuestionType.END;

    let options : GenericSelectOption[] = []
    if (conditionOptions) {
        options = conditionOptions.map((conditionOption: ConditionOptionType) => {
            return {
                value: conditionOption.value,
                displayName: conditionOption.displayName
            }
        })
    }


    // console.log("[QuestionWorkflow] orderedQuestions: ", orderedQuestions);
    // console.log("[QuestionWorkflow] question: ", question);
    // console.log("[QuestionWorkflow] conditions: ", conditions);
    // console.log("[QuestionWorkflow] condition options: ", conditionOptions);
    // console.log("[QuestionWorkflow] editing form questions: ", editingForm?.questions);
    console.log("[QuestionWorkflow] conditions: ", conditions);


    return (
        <div className="question-workflow-short">
            {isProhibited &&
                
                <div className="question-workflow-prohibited">
                    You can not create workflow for this type of question.
                </div>
            }

            {!isProhibited && conditions && conditions.length > 0 &&
                <div className="question-workflow-header">
                    <span>When </span>
                    <div className="question-workflow-question">
                        <FormQuestionTag type={question.type} />
                        <div className="question-workflow-question-title">
                            {question.title}
                        </div>
                    </div>
                </div>
            }

            {conditions && conditions.length > 0 && conditions.map((condition: Condition, conditionIndex: number) => {

                const optionsIndex = options ? options.findIndex((option) => {
                    return option.value === condition.type;
                }) : 0;

                // const options = conditionOptions && conditionOptions.filter((option) => {
                return (
                    <>
                        {/* option type */}
                        {conditionIndex > 0 &&
                            <div className="question-workflow-condition-divider">
                                - or -
                            </div>
                        }
                        <div className="question-workflow-short-condition">
                            {/* {conditionOptions &&
                                <select
                                    key={conditionIndex + "-type"}
                                    onChange={(e: any) => handleChangeOption(e.target.value, conditionIndex)}
                                    data-testid={"condition-type#" + conditionIndex}
                                >
                                    {conditionOptions.map((conditionOption: ConditionOptionType, index) => {
                                        return (
                                            <option 
                                                key={conditionIndex + "-" + index + "-type"}
                                                value={conditionOption.value}
                                                selected={condition.type === conditionOption.value}
                                            >
                                                {conditionOption.displayName}
                                            </option>
                                        )
                                    })}
                                </select>
                            } */}

                            {options &&
                                <GenericSelect
                                    selected={options[optionsIndex]}
                                    options={options}
                                    onChange={(value) => handleChangeOption(value, conditionIndex)}
                                />
                            }
                                {/* <select
                                    key={conditionIndex + "-type"}
                                    onChange={(e: any) => handleChangeOption(e.target.value, conditionIndex)}
                                    data-testid={"condition-type#" + conditionIndex}
                                >
                                    {conditionOptions.map((conditionOption: ConditionOptionType, index) => {
                                        return (
                                            <option 
                                                key={conditionIndex + "-" + index + "-type"}
                                                value={conditionOption.value}
                                                selected={condition.type === conditionOption.value}
                                            >
                                                {conditionOption.displayName}
                                            </option>
                                        )
                                    })}
                                </select> */}
                            
                        </div>
                    
                        {/* values */}
                        <div className="question-workflow-short-values">
                            {(
                                () => {
                                    switch(question.type) {
                                        case QuestionType.SHORT:
                                        case QuestionType.LONG:
                                        case QuestionType.EMAIL:
                                        case QuestionType.WEBSITE:
                                        case QuestionType.NUMBER:
                                        case QuestionType.PHONE:
                                        case QuestionType.NPS:
                                        case QuestionType.SCORE:
                                        case QuestionType.RATING:
                                            return (
                                                <>
                                                    {condition.type === "is_between" &&
                                                        <>
                                                            <input
                                                                key={conditionIndex + "-low-value"}
                                                                type="text"
                                                                value={condition?.values[0] || ""}
                                                                onChange={(e: any) => {
                                                                    handleChangeConditionMultiValue(
                                                                        e.target.value,
                                                                        conditionIndex,
                                                                        VALUE_LOW
                                                                    );
                                                                }}
                                                                data-testid={"condition-value-low#" + conditionIndex}
                                                            />

                                                            and

                                                            <input
                                                                key={conditionIndex + "-high-value"}
                                                                type="text"
                                                                value={condition?.values[1] || ""}
                                                                onChange={(e: any) => {
                                                                    handleChangeConditionMultiValue(
                                                                        e.target.value,
                                                                        conditionIndex,
                                                                        VALUE_HIGH
                                                                    )                                                            
                                                                }}
                                                                data-testid={"condition-value-high#" + conditionIndex}

                                                            />
                                                        </>
                                                    }
                                                    {(condition.type !== "is_empty" && 
                                                        condition.type !== "is_not_empty" &&
                                                        condition.type !== "is_between"
                                                     ) &&
                                                        <input
                                                            key={conditionIndex + "-value"}
                                                            type="text"
                                                            value={condition?.values[0] || ""}
                                                            onChange={(e: any) => {
                                                                handleChangeConditionValue(e.target.value, conditionIndex);
                                                            }}
                                                            data-testid={"condition-value#" + conditionIndex}
                                                        />
                                                    }
                                                </>
                                            )
                                        case QuestionType.MULTIPLE:
                                            return (
                                                <div className="question-workflow-choices">
                                                    {condition.type !== "is_empty" && condition.type !== "is_not_empty" &&
                                                        <>
                                                            {choices && choices.map((choice: string, choiceIndex: number) => {
                                                                    return (
                                                                        <div
                                                                            key={conditionIndex + "-" + choiceIndex + "-choice"} 
                                                                            onClick={() => handleSelectChoice(choice, conditionIndex)}
                                                                            className={"question-workflow-choice" + 
                                                                                (isChoiceSelected(choice, conditionIndex) ?
                                                                                " question-workflow-choice-selected" :
                                                                                "")
                                                                            }
                                                                            data-testid={
                                                                                "condition-choice#" + conditionIndex + ":" + choice
                                                                            }
                                                                        >
                                                                            {choice}
                                                                        </div>
                                                                    )
                                                                }
                                                            )}
                                                        </>
                                                    }
                                                </div>
                                            )     
                                        case QuestionType.DATE:
                                            return (
                                                <>
                                                    {condition.type === "is_between" &&
                                                        <>
                                                            <div 
                                                                className="question-date-picker"
                                                                data-testid={"condition-date-low#" + conditionIndex}
                                                            >
                                                                <DatePicker
                                                                    key={conditionIndex + "-low-date"}
                                                                    selected={condition?.values[0] ? new Date(condition?.values[0]) : new Date()}
                                                                    onChange={(date) => {
                                                                        if (date) {  
                                                                            handleChangeConditionMultiValue(
                                                                                date.toISOString(),
                                                                                conditionIndex,
                                                                                VALUE_LOW
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </div>

                                                            <div>and</div>

                                                            <div 
                                                                className="question-date-picker"
                                                                data-testid={"condition-date-high#" + conditionIndex}
                                                            >
                                                                <DatePicker
                                                                    key={conditionIndex + "-high-date"}
                                                                    selected={condition?.values[1] ? new Date(condition?.values[1]) : new Date()}
                                                                    onChange={(date) => {
                                                                        if (date) {  
                                                                            handleChangeConditionMultiValue(
                                                                                date.toISOString(),
                                                                                conditionIndex,
                                                                                VALUE_HIGH
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </>
                                                    }
                                                    {(condition.type === "is_greater_than" || condition.type === "is_less_than" ||
                                                     condition.type === "is" || condition.type === "is_not") &&
                                                        <div 
                                                            className="question-date-picker"
                                                            data-testid={"condition-date#" + conditionIndex}
                                                        >
                                                            <DatePicker
                                                                key={conditionIndex + "-date"}
                                                                selected={condition?.values[0] ? new Date(condition?.values[0]) : new Date()}
                                                                onChange={(date) => {
                                                                    if (date) {  
                                                                        handleChangeConditionValue(date.toISOString(), conditionIndex);
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    }
                                                    {/* {condition.type === "is_less_than" &&
                                                        <DatePicker
                                                            key={conditionIndex + "-high-date"}
                                                            selected={condition?.values[0] ? new Date(condition?.values[0]) : new Date()}
                                                            onChange={(date) => {
                                                                if (date) {  
                                                                    handleChangeConditionValue(date.toISOString(), conditionIndex);
                                                                }
                                                            }}
                                                        />
                                                    } */}
                                                    {/* {(condition.type !== "is_empty" && 
                                                        condition.type !== "is_not_empty" &&
                                                        condition.type !== "is_between" &&
                                                        condition.type !== "is_greater_than" &&
                                                        condition.type !== "is_less_than"
                                                     ) &&
                                                        <DatePicker
                                                            key={conditionIndex + "-date"}
                                                            selected={condition?.values[0] ? new Date(condition?.values[0]) : new Date()}
                                                            onChange={(date) => {
                                                                if (date) {  
                                                                    handleChangeConditionValue(date.toISOString(), conditionIndex);
                                                                }
                                                            }}
                                                        />
                                                    } */}
                                                </>
                                            )   
                                        case QuestionType.FILE_UPLOAD:
                                            return (
                                                <>
                                                [file something]
                                                </>
                                            )  
                                        case QuestionType.SECTION:
                                        case QuestionType.CONTACT:
                                        case QuestionType.END:
                                        default:
                                            return (<></>)
                                    }
                                }
                                )()
                            }
                        </div>

                        {/* action */}
                        {editingForm && editingForm.questions.length > 1 &&
                            <div className="question-workflow-condition-action">
                                Then jump to
                                <p></p>
                                <QuestionSelect
                                    key={conditionIndex + "-action"}
                                    selected={
                                        conditions[conditionIndex].destinationQuestionId ||
                                        orderedQuestions[0].id
                                    }
                                    questions={orderedQuestions}
                                    onChange={(questionId) => handleChangeDestination(questionId, conditionIndex)}
                                />
                                {/* <select 
                                    key={conditionIndex + "-action"}
                                    onChange={(e: any) => {
                                        handleChangeDestination(e.target.value, conditionIndex);
                                    }}
                                    data-testid={"condition-destination#" + conditionIndex}
                                >
                                    {orderedQuestions
                                        .filter((q: Question) => {
                                            return question.id !== q.id
                                        })
                                        .map((q: Question, index) => {

                                            const dId = conditions[conditionIndex].destinationQuestionId;

                                            return (
                                                <option 
                                                    key={conditionIndex + "-" + q.id + "-action"}
                                                    value={q.id}
                                                    selected={dId === q.id}
                                                >
                                                    {q.title}
                                                </option>
                                            )
                                        })
                                    }
                                </select> */}
                            </div>
                        }

                        {/* control */}  
                        <a 
                            onClick={() => handleDeleteCondition(conditionIndex)} 
                            style={{cursor: "pointer"}}
                            data-testid={"delete-condition#" + conditionIndex}
                        >
                            Delete Condition
                        </a>
                    </>
                )})
                
            }

            {/* {conditions.length === 0 && !isProhibited &&
                <div>
                    No conditions
                </div>
                
            } */}

            {!isProhibited &&
                <> 
                    <div 
                        className="question-workflow-add-condition"
                        onClick={handleAddCondition}
                    >
                        <CirclePlus size={12} color="grey" />
                        <span>Add Condition</span>
                    </div>
                    {/* <a 
                        onClick={handleAddCondition}
                        style={{cursor: "pointer", color: "blue"}}
                    >
                        Add Condition
                    </a> */}
                    {savable &&
                        <button 
                            onClick={handleSave}
                            data-testid="save-conditions"
                        >
                            Save
                        </button>
                    }
                </>
            }

        </div>

    )
}